import { useMemo } from 'react';

// ─── helpers ────────────────────────────────────────────────────────────────

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isoDatePart(isoStr) {
  if (!isoStr) return null;
  return isoStr.split('T')[0];
}

function daysSince(isoStr) {
  if (!isoStr) return null;
  const [y, m, d] = isoStr.split('T')[0].split('-').map(Number);
  const created = new Date(y, m - 1, d);
  const now = new Date();
  const ms = now - created;
  return ms < 0 ? 0 : Math.floor(ms / 86400000);
}

// ─── sub-components ─────────────────────────────────────────────────────────

function StatPill({ color, count, label }) {
  const dotStyle = { backgroundColor: color };
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-gray-50 dark:bg-[#13202E] border border-gray-200 dark:border-[#243348]">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={dotStyle} />
      <span className="text-[var(--wm-text-caption)] font-bold tabular-nums" style={{ color }}>
        {count}
      </span>
      <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] font-medium">
        {label}
      </span>
    </div>
  );
}

const STATUS_BADGE = {
  active:      { label: 'Active',      bg: 'bg-blue-50 dark:bg-blue-900/20',   text: 'text-blue-700 dark:text-blue-400',   border: 'border-blue-200 dark:border-blue-800/40' },
  in_progress: { label: 'In Progress', bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800/40' },
  open:        { label: 'Open',        bg: 'bg-gray-100 dark:bg-[#243348]',    text: 'text-[#64748B] dark:text-[#7D93AE]', border: 'border-gray-200 dark:border-[#243348]' },
};

function StatusBadge({ status }) {
  const meta = STATUS_BADGE[status];
  if (!meta) return null;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[var(--wm-text-caption)] font-semibold flex-shrink-0 ${meta.bg} ${meta.text} ${meta.border}`}>
      {meta.label}
    </span>
  );
}

function WrenchIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
    </svg>
  );
}

// ─── main widget ────────────────────────────────────────────────────────────

const MAX_ROWS = 8;
const ACTIVE_STATUSES = new Set(['active', 'in_progress', 'open']);

export default function ShopFloorWidget() {
  const data = useMemo(() => {
    try {
      const raw = localStorage.getItem('wm-work-orders-v1');
      const orders = JSON.parse(raw || '[]');

      if (!Array.isArray(orders) || orders.length === 0) {
        return { activeCount: 0, inProgressCount: 0, completedTodayCount: 0, rows: [], total: 0, hasData: false };
      }

      const today = todayStr();

      let activeCount = 0;
      let inProgressCount = 0;
      let completedTodayCount = 0;

      for (const wo of orders) {
        const s = wo.status;
        if (s === 'active' || s === 'open') activeCount++;
        if (s === 'in_progress') inProgressCount++;
        if (s === 'completed') {
          const completedOn = isoDatePart(wo.completedDate) || isoDatePart(wo.updatedAt);
          if (completedOn === today) completedTodayCount++;
        }
      }

      // Scrollable list: active / in_progress / open only, oldest first (most urgent)
      const active = orders
        .filter(wo => ACTIVE_STATUSES.has(wo.status))
        .sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return ta - tb; // ascending = oldest first
        });

      const rows = active.slice(0, MAX_ROWS);
      const overflow = active.length - MAX_ROWS;

      return { activeCount, inProgressCount, completedTodayCount, rows, overflow, total: active.length, hasData: true };
    } catch {
      return { activeCount: 0, inProgressCount: 0, completedTodayCount: 0, rows: [], overflow: 0, total: 0, hasData: false };
    }
  }, []);

  const { activeCount, inProgressCount, completedTodayCount, rows, overflow, hasData } = data;

  // ── empty state ──────────────────────────────────────────────────────────
  if (!hasData || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[#13202E] flex items-center justify-center text-[#94A3B8] dark:text-[#3D5470]">
          <WrenchIcon />
        </div>
        <div>
          <p className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">
            Shop floor is clear
          </p>
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">
            No active jobs right now.
          </p>
        </div>

        {/* Still show completed-today count even if nothing active */}
        {completedTodayCount > 0 && (
          <div className="flex items-center gap-1.5 mt-1">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#22c55e' }} />
            <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">
              <span className="font-bold" style={{ color: '#22c55e' }}>{completedTodayCount}</span> completed today
            </span>
          </div>
        )}
      </div>
    );
  }

  // ── normal state ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">

      {/* Summary pills */}
      <div className="flex flex-wrap items-center gap-2">
        <StatPill color="#2E8BF0"  count={activeCount}         label="Active" />
        <StatPill color="#f59e0b"  count={inProgressCount}     label="In Progress" />
        <StatPill color="#22c55e"  count={completedTodayCount} label="Completed Today" />
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 dark:border-[#243348]" />

      {/* Job rows — negative horizontal margin to bleed to card edges */}
      <div className="-mx-4">
        {rows.map((wo, idx) => {
          const title = wo.title || `Work Order #${wo.id}`;
          const age = daysSince(wo.createdAt);
          const isLast = idx === rows.length - 1 && overflow <= 0;

          return (
            <div
              key={wo.id}
              className={[
                'flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors',
                !isLast ? 'border-b border-gray-100 dark:border-[#1D2D40]' : '',
              ].join(' ')}
            >
              {/* Title + meta */}
              <div className="flex-1 min-w-0">
                <p className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate leading-tight">
                  {title}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {wo.assignedTo && (
                    <p className="text-[var(--wm-text-meta)] text-[#7D93AE] truncate leading-tight">
                      {wo.assignedTo}
                    </p>
                  )}
                  {wo.assignedTo && wo.vehicleLabel && (
                    <span className="text-[var(--wm-text-meta)] text-[#94A3B8] dark:text-[#3D5470]">·</span>
                  )}
                  {wo.vehicleLabel && (
                    <p className="text-[var(--wm-text-meta)] text-[#7D93AE] truncate leading-tight">
                      {wo.vehicleLabel}
                    </p>
                  )}
                </div>
              </div>

              {/* Status badge */}
              <StatusBadge status={wo.status} />

              {/* Age */}
              <span className="text-[var(--wm-text-meta)] text-[#7D93AE] flex-shrink-0 w-8 text-right tabular-nums">
                {age != null ? `${age}d` : '—'}
              </span>
            </div>
          );
        })}

        {/* Overflow row */}
        {overflow > 0 && (
          <div className="px-4 py-2.5 text-center">
            <span className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">
              + {overflow} more
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
